<script>
import { useAlert } from 'dashboard/composables';
import aiPromptAPI from 'dashboard/api/aiPrompt';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import SettingsLayout from '../../SettingsLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    SettingsLayout,
    BaseSettingsHeader,
    NextButton,
  },
  data() {
    return {
      isLoading: true,
      isSaving: false,
      systemPrompt: '',
      aiEnabled: false,
    };
  },
  mounted() {
    this.fetchPrompt();
  },
  methods: {
    async fetchPrompt() {
      this.isLoading = true;
      try {
        const response = await aiPromptAPI.show();
        this.systemPrompt = response.data.system_prompt || '';
        this.aiEnabled = response.data.ai_enabled || false;
      } catch (error) {
        useAlert(this.$t('AI_PROMPT.FETCH_ERROR'));
      } finally {
        this.isLoading = false;
      }
    },
    async savePrompt() {
      this.isSaving = true;
      try {
        await aiPromptAPI.update({
          system_prompt: this.systemPrompt,
          ai_enabled: this.aiEnabled,
        });
        useAlert(this.$t('AI_PROMPT.SAVE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('AI_PROMPT.SAVE_ERROR'));
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :no-records-found="false"
  >
    <template #header>
      <BaseSettingsHeader
        title="Treinamento da IA"
        description="Configure o prompt de sistema que define a personalidade e comportamento do atendente virtual."
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6 max-w-4xl">
        <!-- Toggle IA -->
        <div class="flex items-center gap-4 p-4 rounded-lg bg-n-slate-2">
          <button
            class="toggle-switch"
            :class="{ 'toggle-switch--active': aiEnabled }"
            role="switch"
            :aria-checked="aiEnabled"
            @click="aiEnabled = !aiEnabled"
          >
            <span class="toggle-switch__knob" />
          </button>
          <div>
            <p class="font-semibold text-n-slate-12">
              Atendente Virtual IA
            </p>
            <p class="text-sm text-n-slate-11">
              {{ aiEnabled ? 'Ativado — a IA responde automaticamente as mensagens recebidas' : 'Desativado — as mensagens não serão respondidas pela IA' }}
            </p>
          </div>
        </div>

        <!-- Prompt Editor -->
        <div class="flex flex-col gap-2">
          <label class="font-semibold text-n-slate-12">
            Prompt de Sistema (Treinamento)
          </label>
          <p class="text-sm text-n-slate-11 mb-2">
            Este texto define como a IA se comporta. Escreva instruções sobre tom de voz,
            produtos que conhece, regras de atendimento, restrições, etc.
          </p>
          <textarea
            v-model="systemPrompt"
            class="prompt-textarea"
            rows="20"
            placeholder="Exemplo: Você é um assistente virtual da empresa X, especializado em..."
          />
        </div>

        <!-- Save Button -->
        <div class="flex justify-end">
          <NextButton
            blue
            :label="isSaving ? 'Salvando...' : 'Salvar Treinamento'"
            :disabled="isSaving"
            @click="savePrompt"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>

<style scoped>
.prompt-textarea {
  width: 100%;
  min-height: 400px;
  padding: 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 14px;
  line-height: 1.6;
  resize: vertical;
  background-color: #fafafa;
  color: #1e293b;
  transition: border-color 0.2s ease;
}

.prompt-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.toggle-switch {
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: 9999px;
  background-color: #d1d5db;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
}

.toggle-switch--active {
  background-color: #22c55e;
}

.toggle-switch__knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toggle-switch--active .toggle-switch__knob {
  transform: translateX(20px);
}
</style>
